'use client';

import MinusIcon from '@/components/icons/MinusIcon';
import PlusIcon from '@/components/icons/PlusIcon';
import NextLink from '@/components/next-link';
import useCategoryStore from '@/store/category.store';
import { event_category_click, event_button_click } from '@/utils/gtm';
import { getCategoryClickPayload } from '@/utils/gtm/helper';
import { sortJSONKeys } from '@/utils/global';
import { useLocale } from 'next-intl';
import { useState } from 'react';

/** Chevron to match reference (rotates on open) */
const Chevron = ({ locale }) => (
  <svg
    aria-hidden='true'
    viewBox='0 0 24 24'
    width='18'
    height='18'
    className={`${locale !== 'en' && 'rotate-180'} transform transition-transform group-open:rotate-90`}
  >
    <path
      d='M9 6l6 6-6 6'
      fill='none'
      stroke='#1e2b55'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LinksList = ({ category, isMobile, asAccordion = false }) => {
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  const menuTreeData = useCategoryStore((state) => state.menuTreeData);

  const handleLinkClick = (url, name) => {
    if (getCategoryClickPayload({ menuTreeData, link: url })) {
      event_category_click({
        menuTreeData,
        link: url,
        click_location: 'footer',
      });
      return;
    }
    event_button_click({
      button_name: name,
      button_destination: /^(https?:|\/)/.test(url || '') ? url : '/' + url,
      button_location: 'footer',
    });
  };
  if (!category?.[!isMobile ? 'active' : 'mobile-active']) return null;

  const title = category?.[locale === 'en' ? 'title-en' : 'title-ar'];
  const items = sortJSONKeys(category?.value || [], isMobile);

  // DESKTOP: simple section (always open)
  if (!isMobile) {
    return (
      <section className='min-w-0'>
        <h1 className='text-primary mb-2 font-bold'>{title}</h1>
        <ul className='space-y-1.5'>
          {items?.map((s, i) => (
            <li key={i}>
              <NextLink
                className='hover:text-primary text-[15px] hover:underline'
                href={s?.url?.startsWith('/') ? s?.url : `/${s?.url}`}
                onClick={() => handleLinkClick(s?.url, s?.['title-en'])}
              >
                {s?.[locale === 'en' ? 'title-en' : 'title-ar']}
              </NextLink>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  // MOBILE: accordion using <details>/<summary> to match reference
  if (asAccordion) {
    return (
      <section className='py-1'>
        <details className='group'>
          <summary className='text-primary focus-visible:outline-primary/40 flex cursor-pointer list-none items-center justify-between rounded-md px-1 py-3 font-bold focus:outline-none focus-visible:outline'>
            <h1>{title}</h1>
            <Chevron locale={locale} />
          </summary>
          <ul className='space-y-2 px-1 pb-3'>
            {items?.map((s, i) => (
              <li key={i}>
                <NextLink
                  className='hover:text-primary text-[15px] text-black hover:underline'
                  href={s?.url}
                  onClick={() => handleLinkClick(s?.url, s?.['title-en'])}
                >
                  {s?.[locale === 'en' ? 'title-en' : 'title-ar']}
                </NextLink>
              </li>
            ))}
          </ul>
        </details>
      </section>
    );
  }

  // MOBILE (fallback to your old +/- pattern if needed)
  return (
    <div className='max-sm:border-b-border max-sm:col-span-2 max-sm:border-b max-sm:pb-3'>
      <div
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-between max-md:text-sm max-sm:text-xs ${open ? 'mb-3' : 'mb-0 sm:mb-3'
          }`}
      >
        <span className='font-bold text-black'>{title}</span>
        {open ? (
          <MinusIcon className='sm:hidden' />
        ) : (
          <PlusIcon className='sm:hidden' />
        )}
      </div>
      <ul className={`flex-col gap-2.5 ${open ? 'flex' : 'hidden sm:flex!'}`}>
        {items?.map((s, i) => (
          <li
            key={i}
            className='text-dark hover:text-primary w-fit text-xs font-normal duration-200 hover:-translate-x-1 sm:text-sm'
          >
            <NextLink href={s?.url} onClick={() => handleLinkClick(s?.url, s?.['title-en'])}>
              {s?.[locale === 'en' ? 'title-en' : 'title-ar']}
            </NextLink>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LinksList;
